import React, { useState, useEffect, useRef } from 'react';
import { X, MessageSquare, Check, Edit3, Save, XCircle, RefreshCw } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabaseClient';
import { disconnectTwilio } from '../../lib/twilio';
import toast from 'react-hot-toast';

// The automatic Twilio Connect flow is disabled in production for now
// (authorization-grant issues) — manual credentials only. Revisit soon.
const CONNECT_FLOW_ENABLED = import.meta.env.DEV;

interface TwilioSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

// Function to configure webhooks directly via Twilio API
async function configureWebhooksDirectly(accountSid: string, authToken: string, phoneNumber: string) {
  try {
    // Format phone number to E.164
    const formatToE164 = (phone: string) => {
      let cleaned = phone.replace(/\D/g, '');
      if (!cleaned.startsWith('1') && cleaned.length === 10) {
        cleaned = '1' + cleaned;
      }
      return cleaned.startsWith('+') ? cleaned : '+' + cleaned;
    };

    const formattedPhone = formatToE164(phoneNumber);
    
    // First, get the phone number SID
    const credentials = btoa(`${accountSid}:${authToken}`);
    const listResponse = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/IncomingPhoneNumbers.json?PhoneNumber=${encodeURIComponent(formattedPhone)}`,
      {
        headers: {
          'Authorization': `Basic ${credentials}`
        }
      }
    );

    if (!listResponse.ok) {
      throw new Error('Failed to find phone number in Twilio account');
    }

    const listData = await listResponse.json();
    if (!listData.incoming_phone_numbers || listData.incoming_phone_numbers.length === 0) {
      throw new Error(`Phone number ${formattedPhone} not found in your Twilio account`);
    }

    const phoneNumberSid = listData.incoming_phone_numbers[0].sid;
    
    // Configure the webhooks
    const baseUrl = 'https://idvgoctfaarxiryxprpy.supabase.co/functions/v1';
    const voiceUrl = `${baseUrl}/twilio-call-webhook`;
    const statusCallbackUrl = `${baseUrl}/twilio-call-status`;
    const smsUrl = `${baseUrl}/sms-webhook`;

    const updateResponse = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/IncomingPhoneNumbers/${phoneNumberSid}.json`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          VoiceUrl: voiceUrl,
          VoiceMethod: 'POST',
          StatusCallback: statusCallbackUrl,
          StatusCallbackMethod: 'POST',
          SmsUrl: smsUrl,
          SmsMethod: 'POST'
        })
      }
    );

    if (!updateResponse.ok) {
      const errorData = await updateResponse.json();
      throw new Error(errorData.message || 'Failed to update webhook URLs');
    }

    // Create TwiML Application for Voice SDK
    console.log('🔍 Creating TwiML Application for Voice SDK...');
    const twimlAppResponse = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/Applications.json`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          FriendlyName: 'Voice SDK Application (Auto-created)',
          VoiceUrl: `${baseUrl}/twilio-voice-handler`,
          VoiceMethod: 'POST',
          StatusCallback: statusCallbackUrl,
          StatusCallbackMethod: 'POST'
        })
      }
    );

    let applicationSid = null;
    if (twimlAppResponse.ok) {
      const appData = await twimlAppResponse.json();
      applicationSid = appData.sid;
      console.log('✅ TwiML Application created:', applicationSid);
    } else {
      console.warn('⚠️ Failed to create TwiML Application, Voice SDK may not work');
    }

    return {
      success: true,
      voiceUrl,
      statusCallbackUrl,
      applicationSid
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to configure webhooks'
    };
  }
}

export default function TwilioSettings({ isOpen, onClose, onSave }: TwilioSettingsProps) {
  const { selectedBusiness } = useBusiness();
  const [twilioSettings, setTwilioSettings] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfiguringWebhooks, setIsConfiguringWebhooks] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  // Connection method chooser: 'connect' (Twilio Connect, easy) or 'manual' (paste creds).
  const [method, setMethod] = useState<'connect' | 'manual'>('connect');
  const [isConnecting, setIsConnecting] = useState(false);
  // Connect number provisioning
  const [searchAreaCode, setSearchAreaCode] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [purchasingNumber, setPurchasingNumber] = useState<string | null>(null);
  const [numberTab, setNumberTab] = useState<'existing' | 'new'>('existing');
  const [existingNumbers, setExistingNumbers] = useState<any[]>([]);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  
  // Editing states for Twilio fields
  const [editingAccountSid, setEditingAccountSid] = useState(false);
  const [editingAuthToken, setEditingAuthToken] = useState(false);
  const [editingPhoneNumber, setEditingPhoneNumber] = useState(false);
  const [editingCallForwarding, setEditingCallForwarding] = useState(false);
  const [tempAccountSid, setTempAccountSid] = useState('');
  const [tempAuthToken, setTempAuthToken] = useState('');
  const [tempPhoneNumber, setTempPhoneNumber] = useState('');
  const [tempCallForwardingEnabled, setTempCallForwardingEnabled] = useState(false);
  const [tempForwardingNumber, setTempForwardingNumber] = useState('');

  // Load Twilio settings
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadTwilioSettings();
    }
  }, [isOpen, selectedBusiness?.id]);

  const loadTwilioSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('businesses')
        .select(`
          twilio_enabled,
          twilio_status,
          twilio_connection_method,
          twilio_account_sid,
          twilio_auth_token,
          VITE_TWILIO_PHONE_NUMBER,
          twilio_call_forwarding_enabled,
          twilio_forwarding_number,
          twilio_webhooks_configured,
          twilio_application_sid
        `)
        .eq('id', selectedBusiness.id)
        .single();

      if (!error && data) {
        setTwilioSettings(data);
        // The automatic Connect flow is dev-only for now (auth-grant issues in
        // production — revisit later). Production is manual-only.
        if (!CONNECT_FLOW_ENABLED) {
          setMethod('manual');
        } else if (data.twilio_enabled && data.twilio_connection_method === 'manual') {
          // Anyone not fully connected starts on the easy Connect path —
          // leftover credentials from a failed attempt must not flip the
          // default to Manual. Only an ENABLED manual integration shows manual.
          setMethod('manual');
        } else {
          setMethod('connect');
        }
      }
    } catch (error) {
      console.error('Error loading Twilio settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (field: string) => {
    switch (field) {
      case 'accountSid':
        setTempAccountSid(twilioSettings?.twilio_account_sid || '');
        setEditingAccountSid(true);
        break;
      case 'authToken':
        setTempAuthToken(twilioSettings?.twilio_auth_token || '');
        setEditingAuthToken(true);
        break;
      case 'phoneNumber':
        setTempPhoneNumber(twilioSettings?.VITE_TWILIO_PHONE_NUMBER || '');
        setEditingPhoneNumber(true);
        break;
      case 'callForwarding':
        setTempCallForwardingEnabled(twilioSettings?.twilio_call_forwarding_enabled || false);
        setTempForwardingNumber(twilioSettings?.twilio_forwarding_number || '');
        setEditingCallForwarding(true);
        break;
    }
  };

  const cancelEditing = (field: string) => {
    switch (field) {
      case 'accountSid':
        setEditingAccountSid(false);
        setTempAccountSid('');
        break;
      case 'authToken':
        setEditingAuthToken(false);
        setTempAuthToken('');
        break;
      case 'phoneNumber':
        setEditingPhoneNumber(false);
        setTempPhoneNumber('');
        break;
      case 'callForwarding':
        setEditingCallForwarding(false);
        setTempCallForwardingEnabled(false);
        setTempForwardingNumber('');
        break;
    }
  };

  const saveField = async (field: string) => {
    if (!selectedBusiness?.id) return;

    setIsSaving(true);
    try {
      const updateData: any = {};
      
      switch (field) {
        // Editing manual credentials switches the connection method back to
        // 'manual' — otherwise a stale 'connect' method (left over from a prior
        // Twilio Connect authorization) makes every send path use the platform
        // master token against the wrong account.
        case 'accountSid':
          updateData.twilio_account_sid = tempAccountSid.trim();
          updateData.twilio_connection_method = 'manual';
          break;
        case 'authToken':
          updateData.twilio_auth_token = tempAuthToken.trim();
          updateData.twilio_connection_method = 'manual';
          break;
        case 'phoneNumber':
          updateData.VITE_TWILIO_PHONE_NUMBER = tempPhoneNumber.trim();
          break;
        case 'callForwarding':
          updateData.twilio_call_forwarding_enabled = tempCallForwardingEnabled;
          updateData.twilio_forwarding_number = tempForwardingNumber;
          break;
      }

      const { error } = await supabase
        .from("businesses")
        .update(updateData)
        .eq("id", selectedBusiness.id);

      if (error) throw error;

      // Update local state
      setTwilioSettings((prev: any) => ({ ...prev, ...updateData }));
      
      // Exit editing mode
      cancelEditing(field);
      
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error("Error updating Twilio settings:", error);
      toast.error('Failed to update settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivateTwilio = async () => {
    if (!selectedBusiness?.id || !twilioSettings) return;

    setIsSaving(true);
    setIsConfiguringWebhooks(true);
    const wasAlreadyEnabled = Boolean(twilioSettings.twilio_enabled);

    try {
      const { data, error } = await supabase.functions.invoke('twilio-reconfigure', {
        body: { businessId: selectedBusiness.id },
      });

      // Non-2xx responses often still include our JSON body in `data`.
      if (error || !data?.success || !data?.application_sid) {
        throw new Error(
          data?.error || error?.message || 'Reconnect failed — Voice application was not created.',
        );
      }

      setTwilioSettings((prev: any) => ({
        ...prev,
        twilio_enabled: true,
        twilio_status: 'active',
        twilio_webhooks_configured: true,
        twilio_application_sid: data.application_sid,
      }));

      toast.success(
        wasAlreadyEnabled
          ? `Voice ready — app ${String(data.application_sid).slice(0, 10)}…`
          : 'Twilio integration activated successfully',
      );
      onSave?.();
    } catch (error) {
      console.error('Error activating/reconfiguring Twilio:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to reconfigure Twilio Voice',
      );
    } finally {
      setIsSaving(false);
      setIsConfiguringWebhooks(false);
    }
  };

  const handleConnectTwilio = async () => {
    if (!selectedBusiness?.id) return;
    setIsConnecting(true);
    try {
      // Always send an explicit returnUrl: without it the callback falls back
      // to APP_PUBLIC_URL (production), which strands localhost/dev sessions
      // on the production login page after authorizing.
      const { data, error } = await supabase.functions.invoke('twilio-connect-start', {
        body: {
          businessId: selectedBusiness.id,
          returnUrl: (() => {
            const path = window.location.pathname || '/app-store';
            const backToStore =
              path.startsWith('/app-store') || path.startsWith('/integrations');
            return backToStore
              ? `${window.location.origin}/app-store?tab=integrations`
              : `${window.location.origin}${path}`;
          })(),
        },
      });
      if (error) throw error;
      if (!data?.authorizeUrl) throw new Error('No authorize URL returned');
      // Hand off to Twilio's authorization page; it redirects back to our callback.
      window.location.href = data.authorizeUrl;
    } catch (error) {
      console.error('Error starting Twilio Connect:', error);
      toast.error('Could not start Twilio connection');
      setIsConnecting(false);
    }
  };

  // supabase.functions.invoke hides non-2xx response bodies behind
  // error.context — unwrap so we surface the REAL failure reason.
  const invokeWithDetail = async (fn: string, body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke(fn, { body });
    if (error) {
      let detail = '';
      try {
        detail = (await (error as { context?: Response }).context?.json())?.error || '';
      } catch { /* keep generic */ }
      throw new Error(detail || error.message);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const loadExistingNumbers = async () => {
    if (!selectedBusiness?.id) return;
    setIsLoadingExisting(true);
    try {
      const data = await invokeWithDetail('twilio-list-numbers', { businessId: selectedBusiness.id });
      setExistingNumbers(data?.numbers || []);
    } catch (e) {
      console.error('Could not list existing numbers:', e);
      toast.error(e instanceof Error ? e.message : 'Could not list existing numbers');
      setExistingNumbers([]);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  // Auto-load the account's existing numbers once it's connected (so the
  // "Existing" tab is populated and we can default to it when they have numbers).
  useEffect(() => {
    if (
      isOpen &&
      method === 'connect' &&
      !twilioSettings?.twilio_enabled &&
      twilioSettings?.twilio_connection_method === 'connect' &&
      twilioSettings?.twilio_account_sid
    ) {
      loadExistingNumbers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, method, twilioSettings?.twilio_connection_method, twilioSettings?.twilio_account_sid, twilioSettings?.twilio_enabled]);

  const handleAssignNumber = async (sid: string, phoneNumber: string) => {
    if (!selectedBusiness?.id) return;
    setPurchasingNumber(phoneNumber);
    try {
      const data = await invokeWithDetail('twilio-purchase-number', {
        businessId: selectedBusiness.id,
        phoneNumberSid: sid,
      });
      toast.success(`Number ${data.phoneNumber} connected!`);
      await loadTwilioSettings();
      onSave?.();
    } catch (e) {
      console.error('Number assign failed:', e);
      toast.error(e instanceof Error ? e.message : 'Could not assign number');
    } finally {
      setPurchasingNumber(null);
    }
  };

  // Manual method: set the chosen existing number as the from-number (Activate
  // then configures its webhooks). Uses the business's own stored creds.
  const handleSelectManualNumber = async (phoneNumber: string) => {
    if (!selectedBusiness?.id) return;
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ VITE_TWILIO_PHONE_NUMBER: phoneNumber })
        .eq('id', selectedBusiness.id);
      if (error) throw error;
      setTwilioSettings((prev: any) => ({ ...prev, VITE_TWILIO_PHONE_NUMBER: phoneNumber }));
      toast.success(`Selected ${phoneNumber}`);
    } catch (e) {
      console.error('Could not select number:', e);
      toast.error('Could not select number');
    }
  };

  // Numbers already shown for the current query, so "Show different numbers"
  // can surface a fresh batch (Twilio's availability API has no paging).
  const seenNumbersRef = useRef<Set<string>>(new Set());

  const handleSearchNumbers = async (refresh = false) => {
    if (!selectedBusiness?.id) return;
    setIsSearching(true);
    try {
      if (!refresh) seenNumbersRef.current = new Set();
      const data = await invokeWithDetail('twilio-search-numbers', {
        businessId: selectedBusiness.id,
        areaCode: searchAreaCode || undefined,
      });
      const all: Array<{ phoneNumber: string }> = data?.numbers || [];
      let next = all;
      if (refresh) {
        const unseen = all.filter((n) => !seenNumbersRef.current.has(n.phoneNumber));
        if (unseen.length > 0) {
          next = unseen;
        } else if (all.length > 0) {
          toast('No new numbers available right now — showing the current batch.');
        }
      }
      next.forEach((n) => seenNumbersRef.current.add(n.phoneNumber));
      setAvailableNumbers(next);
      if (all.length === 0) toast('No numbers found — try a different area code');
    } catch (e) {
      console.error('Number search failed:', e);
      toast.error(e instanceof Error ? e.message : 'Number search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const handlePurchaseNumber = async (phoneNumber: string) => {
    if (!selectedBusiness?.id) return;
    setPurchasingNumber(phoneNumber);
    try {
      const data = await invokeWithDetail('twilio-purchase-number', {
        businessId: selectedBusiness.id,
        phoneNumber,
      });
      toast.success(`Number ${data.phoneNumber} connected!`);
      setAvailableNumbers([]);
      await loadTwilioSettings();
      onSave?.();
    } catch (e) {
      console.error('Number purchase failed:', e);
      toast.error(e instanceof Error ? e.message : 'Could not purchase number');
    } finally {
      setPurchasingNumber(null);
    }
  };

  const handleDisconnectTwilio = async () => {
    if (!selectedBusiness?.id) return;
    
    setIsDisconnecting(true);
    try {
      // disconnectTwilio takes an object — passing a bare string left businessId
      // undefined, so the DB update matched nothing and nothing disconnected.
      const result = await disconnectTwilio({ businessId: selectedBusiness.id });
      if (!result.success) {
        throw new Error(result.error || 'Disconnect failed');
      }

      // Reset local state (and method) so the connection chooser reappears.
      setTwilioSettings((prev: any) => ({
        ...prev,
        twilio_enabled: false,
        twilio_status: null,
        twilio_account_sid: null,
        twilio_auth_token: null,
        VITE_TWILIO_PHONE_NUMBER: null,
        twilio_connection_method: 'manual',
      }));
      setMethod('connect');
      setAvailableNumbers([]);

      toast.success('Twilio integration disconnected successfully');
      onSave?.();
      
    } catch (error) {
      console.error("Error disconnecting Twilio:", error);
      toast.error('Failed to disconnect Twilio integration');
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
      <div className="bg-white rounded-[20px] shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Twilio Integration</h2>
                <p className="text-sm text-gray-400 mt-1">Configure SMS and voice communications</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : twilioSettings?.twilio_connection_method === 'nexus' ? (
            // A Nexus phone number lives in these same columns (platform-owned
            // subaccount). Never show or let the user edit those credentials here.
            <div className="p-5 bg-indigo-50 border border-indigo-100 rounded-2xl space-y-2">
              <p className="text-sm font-semibold text-gray-900">You're using a Nexus phone number</p>
              <p className="text-sm text-gray-600">
                {twilioSettings.VITE_TWILIO_PHONE_NUMBER ? (
                  <>
                    <span className="font-mono font-medium text-gray-900">{twilioSettings.VITE_TWILIO_PHONE_NUMBER}</span> is
                    provided and billed by Nexus, so there's nothing to configure here.
                  </>
                ) : (
                  'Your number is provided and billed by Nexus, so there is nothing to configure here.'
                )}
              </p>
              <p className="text-xs text-gray-500">
                Manage it from the <span className="font-semibold">Nexus Phone Number</span> card in Integrations. To use your
                own Twilio account instead, release the Nexus number first.
              </p>
            </div>
          ) : twilioSettings ? (
            <div className="space-y-6">
              {/* Connection method chooser (Connect option is dev-only for now) */}
              {!twilioSettings.twilio_enabled && CONNECT_FLOW_ENABLED && (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setMethod('connect')}
                    className={`text-left p-4 rounded-xl border transition-colors ${
                      method === 'connect'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <p className="text-sm font-semibold text-gray-900">Connect Twilio</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Authorize with one click. Recommended.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod('manual')}
                    className={`text-left p-4 rounded-xl border transition-colors ${
                      method === 'manual'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <p className="text-sm font-semibold text-gray-900">Enter manually</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Paste your own Account SID &amp; token. Advanced.
                    </p>
                  </button>
                </div>
              )}

              {/* Connect path */}
              {method === 'connect' && !twilioSettings.twilio_enabled && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                  {twilioSettings.twilio_connection_method === 'connect' && twilioSettings.twilio_account_sid ? (
                    <>
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        <span className="text-sm font-medium text-gray-900">Twilio account connected</span>
                      </div>
                      <p className="text-xs text-gray-400 font-mono">{twilioSettings.twilio_account_sid}</p>

                      {/* Number picker — finish setup by reusing an existing number or getting a new one */}
                      <div className="pt-2 border-t border-gray-200 space-y-3">
                        <p className="text-sm text-gray-600">Choose a phone number to finish setup:</p>

                        {/* Existing | New tabs */}
                        <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                          <button
                            type="button"
                            onClick={() => setNumberTab('existing')}
                            className={`rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors ${numberTab === 'existing' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                          >
                            Existing number
                          </button>
                          <button
                            type="button"
                            onClick={() => setNumberTab('new')}
                            className={`rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors ${numberTab === 'new' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                          >
                            Get a new number
                          </button>
                        </div>

                        {numberTab === 'existing' ? (
                          <>
                            {isLoadingExisting ? (
                              <p className="text-xs text-gray-400">Loading your numbers…</p>
                            ) : existingNumbers.length === 0 ? (
                              <div className="text-xs text-gray-500 space-y-2">
                                <p>
                                  Numbers in your main Twilio account aren't available here. The one-click
                                  Connect creates a separate, secure Twilio subaccount, and Twilio keeps your
                                  existing numbers in your main account (it doesn't share them).
                                </p>
                                <p>
                                  To <span className="font-semibold">reuse an existing number</span>, click
                                  "Disconnect &amp; start over" below, then choose
                                  <span className="font-semibold"> Enter manually</span> and paste your own
                                  Account SID + token. Otherwise, use
                                  <span className="font-semibold"> "Get a new number."</span>
                                </p>
                              </div>
                            ) : (
                              <>
                                <p className="text-xs text-gray-500">
                                  Selecting a number points its SMS &amp; voice webhooks to this app.
                                </p>
                                <div className="space-y-2 max-h-56 overflow-y-auto">
                                  {existingNumbers.map((n) => (
                                    <div
                                      key={n.sid}
                                      className="flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 rounded-lg"
                                    >
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 font-mono">{n.phoneNumber}</p>
                                        <p className="text-xs text-gray-400 truncate">
                                          {n.friendlyName || 'Owned number'}
                                          {!n.smsCapable ? ' · no SMS' : ''}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleAssignNumber(n.sid, n.phoneNumber)}
                                        disabled={!!purchasingNumber}
                                        className="shrink-0 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-lg text-xs font-semibold transition-colors"
                                      >
                                        {purchasingNumber === n.phoneNumber ? 'Connecting…' : 'Use this number'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                inputMode="numeric"
                                value={searchAreaCode}
                                onChange={(e) => setSearchAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                                placeholder="Area code (e.g. 604)"
                                className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <button
                                type="button"
                                onClick={() => handleSearchNumbers(false)}
                                disabled={isSearching}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-semibold transition-colors"
                              >
                                {isSearching ? 'Searching…' : 'Search'}
                              </button>
                            </div>
                            {availableNumbers.length > 0 && (
                              <>
                                <div className="space-y-2 max-h-56 overflow-y-auto">
                                  {availableNumbers.map((n) => (
                                    <div
                                      key={n.phoneNumber}
                                      className="flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 rounded-lg"
                                    >
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 font-mono">{n.phoneNumber}</p>
                                        <p className="text-xs text-gray-400 truncate">
                                          {[n.locality, n.region].filter(Boolean).join(', ') || 'Local number'}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handlePurchaseNumber(n.phoneNumber)}
                                        disabled={!!purchasingNumber}
                                        className="shrink-0 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-lg text-xs font-semibold transition-colors"
                                      >
                                        {purchasingNumber === n.phoneNumber ? 'Buying…' : 'Use this number'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleSearchNumbers(true)}
                                  disabled={isSearching}
                                  className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                                >
                                  <RefreshCw className={`h-3.5 w-3.5 ${isSearching ? 'animate-spin' : ''}`} />
                                  {isSearching ? 'Finding more…' : 'Show different numbers'}
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>

                      {/* Always allow bailing out / restarting from this step. */}
                      <div className="pt-2 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={handleDisconnectTwilio}
                          disabled={isDisconnecting}
                          className="text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                        >
                          {isDisconnecting ? 'Disconnecting…' : 'Disconnect & start over'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">
                        You'll be sent to Twilio to authorize access, then brought right back. Twilio bills your
                        own account directly — we never store your password or token.
                      </p>
                      <button
                        type="button"
                        onClick={handleConnectTwilio}
                        disabled={isConnecting}
                        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl text-sm font-semibold transition-colors flex items-center gap-2"
                      >
                        {isConnecting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Connecting…
                          </>
                        ) : (
                          'Connect Twilio'
                        )}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Manual credential fields (also shown for already-active connections so they stay editable) */}
              {(method === 'manual' || twilioSettings.twilio_enabled) && (
              <>
              {/* Account SID */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Account SID</span>
                  <div className="flex items-center space-x-2">
                    {!editingAccountSid && twilioSettings.twilio_account_sid && <Check className="h-4 w-4 text-green-500" />}
                    <button
                      onClick={() => editingAccountSid ? cancelEditing('accountSid') : startEditing('accountSid')}
                      className="text-gray-400 hover:text-gray-600"
                      disabled={isSaving}
                    >
                      {editingAccountSid ? <XCircle className="h-4 w-4" /> : <Edit3 className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {editingAccountSid ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={tempAccountSid}
                      onChange={(e) => setTempAccountSid(e.target.value)}
                      className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter Account SID"
                    />
                    <button
                      onClick={() => saveField('accountSid')}
                      disabled={isSaving || !tempAccountSid.trim()}
                      className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-lg transition-colors"
                    >
                      <Save className="h-4 w-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-900 font-mono text-sm">
                    {twilioSettings.twilio_account_sid || 'Not configured'}
                  </p>
                )}
              </div>

              {/* Auth Token */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Auth Token</span>
                  <div className="flex items-center space-x-2">
                    {!editingAuthToken && twilioSettings.twilio_auth_token && <Check className="h-4 w-4 text-green-500" />}
                    <button
                      onClick={() => editingAuthToken ? cancelEditing('authToken') : startEditing('authToken')}
                      className="text-gray-400 hover:text-gray-600"
                      disabled={isSaving}
                    >
                      {editingAuthToken ? <XCircle className="h-4 w-4" /> : <Edit3 className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {editingAuthToken ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="password"
                      value={tempAuthToken}
                      onChange={(e) => setTempAuthToken(e.target.value)}
                      className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter Auth Token"
                    />
                    <button
                      onClick={() => saveField('authToken')}
                      disabled={isSaving || !tempAuthToken.trim()}
                      className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-lg transition-colors"
                    >
                      <Save className="h-4 w-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-900 font-mono text-sm">
                    {twilioSettings.twilio_auth_token ? '••••••••••••••••' : 'Not configured'}
                  </p>
                )}
              </div>

              {/* Phone Number */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Phone Number</span>
                  <div className="flex items-center space-x-2">
                    {!editingPhoneNumber && twilioSettings.VITE_TWILIO_PHONE_NUMBER && <Check className="h-4 w-4 text-green-500" />}
                    <button
                      onClick={() => editingPhoneNumber ? cancelEditing('phoneNumber') : startEditing('phoneNumber')}
                      className="text-gray-400 hover:text-gray-600"
                      disabled={isSaving}
                    >
                      {editingPhoneNumber ? <XCircle className="h-4 w-4" /> : <Edit3 className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {editingPhoneNumber ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="tel"
                      value={tempPhoneNumber}
                      onChange={(e) => setTempPhoneNumber(e.target.value)}
                      className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+1234567890"
                    />
                    <button
                      onClick={() => saveField('phoneNumber')}
                      disabled={isSaving || !tempPhoneNumber.trim()}
                      className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-lg transition-colors"
                    >
                      <Save className="h-4 w-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-900 font-mono text-sm">
                    {twilioSettings.VITE_TWILIO_PHONE_NUMBER || 'Not configured'}
                  </p>
                )}
              </div>

              {/* Pick from existing numbers (manual method has full account access) */}
              {method === 'manual' &&
                !twilioSettings.twilio_enabled &&
                twilioSettings.twilio_account_sid &&
                twilioSettings.twilio_auth_token && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Or pick from your Twilio numbers
                      </span>
                      <button
                        type="button"
                        onClick={loadExistingNumbers}
                        disabled={isLoadingExisting}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50"
                      >
                        {isLoadingExisting ? 'Loading…' : 'Load my numbers'}
                      </button>
                    </div>
                    {existingNumbers.length > 0 && (
                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {existingNumbers.map((n) => (
                          <div
                            key={n.sid}
                            className="flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 rounded-lg"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 font-mono">{n.phoneNumber}</p>
                              <p className="text-xs text-gray-400 truncate">
                                {n.friendlyName || 'Owned number'}
                                {!n.smsCapable ? ' · no SMS' : ''}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleSelectManualNumber(n.phoneNumber)}
                              className="shrink-0 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors"
                            >
                              Use this
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

              {/* Call Forwarding */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Call Forwarding</span>
                  <div className="flex items-center space-x-2">
                    {!editingCallForwarding && <Check className="h-4 w-4 text-green-500" />}
                    <button
                      onClick={() => editingCallForwarding ? cancelEditing('callForwarding') : startEditing('callForwarding')}
                      className="text-gray-400 hover:text-gray-600"
                      disabled={isSaving}
                    >
                      {editingCallForwarding ? <XCircle className="h-4 w-4" /> : <Edit3 className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {editingCallForwarding ? (
                  <div className="space-y-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={tempCallForwardingEnabled}
                        onChange={(e) => setTempCallForwardingEnabled(e.target.checked)}
                        className="rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Enable call forwarding</span>
                    </label>
                    {tempCallForwardingEnabled && (
                      <input
                        type="tel"
                        value={tempForwardingNumber}
                        onChange={(e) => setTempForwardingNumber(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Forwarding phone number"
                      />
                    )}
                    <button
                      onClick={() => saveField('callForwarding')}
                      disabled={isSaving}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-lg transition-colors text-white text-sm"
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="text-gray-900 text-sm">
                      {twilioSettings.twilio_call_forwarding_enabled ? (
                        <span className="text-green-500">Enabled</span>
                      ) : (
                        <span className="text-gray-400">Disabled</span>
                      )}
                    </p>
                    {twilioSettings.twilio_call_forwarding_enabled && twilioSettings.twilio_forwarding_number && (
                      <p className="text-gray-900 font-mono text-sm">
                        {twilioSettings.twilio_forwarding_number}
                      </p>
                    )}
                  </div>
                )}
              </div>
              </>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400">No Twilio settings found</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {twilioSettings && twilioSettings.twilio_connection_method !== 'nexus' && (() => {
          // Allow disconnecting whenever a connection exists OR is mid-setup
          // (Connect authorized but no number chosen yet), so users can restart.
          const connectInProgress =
            twilioSettings.twilio_connection_method === 'connect' && !!twilioSettings.twilio_account_sid;
          const showDisconnect = twilioSettings.twilio_enabled || connectInProgress;
          return (
          <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
            {twilioSettings.twilio_enabled ? (
              <>
                <button
                  onClick={handleActivateTwilio}
                  disabled={isSaving || isConfiguringWebhooks || isDisconnecting || !twilioSettings.twilio_account_sid || !twilioSettings.twilio_auth_token || !twilioSettings.VITE_TWILIO_PHONE_NUMBER}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-2xl transition-colors flex items-center gap-2"
                >
                  {isSaving || isConfiguringWebhooks ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {isConfiguringWebhooks ? 'Reconfiguring...' : 'Reconnecting...'}
                    </>
                  ) : (
                    'Reconnect / Reconfigure'
                  )}
                </button>
                <button
                  onClick={handleDisconnectTwilio}
                  disabled={isDisconnecting || isSaving || isConfiguringWebhooks}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded-2xl transition-colors flex items-center gap-2"
                >
                  {isDisconnecting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Disconnecting...
                    </>
                  ) : (
                    'Disconnect Twilio'
                  )}
                </button>
              </>
            ) : showDisconnect ? (
              <button
                onClick={handleDisconnectTwilio}
                disabled={isDisconnecting}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded-2xl transition-colors flex items-center gap-2"
              >
                {isDisconnecting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Disconnecting...
                  </>
                ) : (
                  'Disconnect & start over'
                )}
              </button>
            ) : method === 'manual' ? (
              <button
                onClick={handleActivateTwilio}
                disabled={isSaving || isConfiguringWebhooks || !twilioSettings.twilio_account_sid || !twilioSettings.twilio_auth_token || !twilioSettings.VITE_TWILIO_PHONE_NUMBER}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-2xl transition-colors flex items-center gap-2"
              >
                {isSaving || isConfiguringWebhooks ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {isConfiguringWebhooks ? 'Configuring...' : 'Activating...'}
                  </>
                ) : (
                  'Activate Integration'
                )}
              </button>
            ) : null}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
          );
        })()}
        {twilioSettings?.twilio_connection_method === 'nexus' && (
          <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}